/** Admin: every user, with verification and suspension. */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { fmtDate } from '../../core/format.js';
import { ROLE_LABEL } from '../../core/constants.js';
import { session } from '../../core/session.js';
import { reload } from '../../app/shell.js';
import { empty, pageHead, statusTag, table, tag, who } from '../../ui/components.js';
import { field } from '../../ui/forms.js';
import { closeModal, confirmBox, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';
import { PAGE, filterSelect, pager, queryOf, searchBox } from './shared.js';

const users = {
  path: '/admin/users',
  roles: ['admin'],
  title: 'All users',
  async load(ctx) {
    const query = queryOf(ctx);
    const { users: list } = await api.get('/admin/users', { signal: ctx.signal, query: { ...query, limit: PAGE } });
    return { list, query };
  },
  view({ list, query }) {
    const roles = Object.entries(ROLE_LABEL).map(([v, t]) => [v, t]);
    return html`${pageHead('All users', 'Search, verify freelancers, and suspend accounts that break the rules.')}
      <div class="row mb">${searchBox('q', query.q, 'Search name, email or phone')}${filterSelect('Role', 'role', roles, query.role)}${filterSelect('Status', 'status', [['active', 'Active'], ['suspended', 'Suspended']], query.status)}${filterSelect('Verification', 'verification', [['verified', 'Verified'], ['pending', 'Pending'], ['unverified', 'Not verified']], query.verification)}</div>
      ${list.length
        ? table(['User', 'Contact', 'Role', 'Status', 'Verification', 'Joined', ''], list.map((u) => [
          who(u.name, u.org_role || ''), html`<div class="xs">${u.email}<br>${u.phone}${u.phone_verified ? '' : html` ${tag('Phone not verified', 'wn')}`}</div>`, ROLE_LABEL[u.role] || u.role, statusTag(u.status),
          u.role === 'professional' ? statusTag(u.verification) : html`<span class="mu">-</span>`, fmtDate(u.created_at), actions(u),
        ]))
        : empty('users', 'No users match', 'Try a different search or clear a filter.')}
      ${pager('/admin/users', query, list.length)}`;
  },
};

function actions(u) {
  if (u.id === session.user.id) return html`<span class="mu xs">You</span>`;
  return html`<div class="row nw">${u.role === 'professional' ? html`<button type="button" class="btn ${u.verification === 'verified' ? 'ghost' : ''} sm" data-act="user.verify" data-id="${u.id}" data-name="${u.name}" data-on="${u.verification !== 'verified'}">${u.verification === 'verified' ? 'Unverify' : 'Verify'}</button>` : ''}
    ${u.role !== 'admin' || u.status === 'suspended' ? html`<button type="button" class="btn ${u.status === 'active' ? 'danger' : 'ghost'} sm" data-act="user.status" data-id="${u.id}" data-name="${u.name}" data-status="${u.status === 'active' ? 'suspended' : 'active'}">${u.status === 'active' ? 'Suspend' : 'Reactivate'}</button>` : ''}</div>`;
}

register({
  'user.verify': async (el) => {
    const on = el.dataset.on === 'true';
    if (!(await confirmBox({ title: `${on ? 'Verify' : 'Unverify'} ${el.dataset.name}?`, text: on ? 'They can apply to requirements and teach batches. They are notified.' : 'They lose access to applying and teaching until verified again.', label: on ? 'Verify' : 'Unverify', danger: !on }))) return;
    await api.post(`/admin/users/${el.dataset.id}/verify`, { body: { verified: on } });
    toast(on ? 'Verified' : 'Verification removed', 'shield');
    await reload();
  },
  'user.status': (el) => {
    const suspend = el.dataset.status === 'suspended';
    openModal({
      title: `${suspend ? 'Suspend' : 'Reactivate'} ${el.dataset.name}`,
      body: html`<form id="status-form" data-submit="user.status.send" data-id="${el.dataset.id}" data-status="${el.dataset.status}" novalidate>${field('us-reason', 'Reason (kept in the audit log)', { name: 'reason', minlen: 3, maxlength: 300 })}</form>
        <p class="note">${suspend ? 'They are signed out everywhere immediately.' : 'They can log in again.'}</p>`,
      foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="status-form" class="btn ${suspend ? 'danger' : ''}">${suspend ? 'Suspend account' : 'Reactivate'}</button>`,
    });
  },
  'user.status.send': (form) => submitForm(form, async ({ reason }) => {
    await api.post(`/admin/users/${form.dataset.id}/status`, { body: { status: form.dataset.status, reason } });
    closeModal(false);
    toast('Account updated', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
});

export const routes = [users];
